import {
    DynamicComponentExitReason,
    DynamicComponentOptions,
    DynamicComponentResult,
} from '@jack-henry/frontend-utils/lit/dynamic-component-registry';
import { DeepPartial } from '@jack-henry/frontend-utils/types';
import { Nullable } from '@treasury/utils';
import type { TemplateResult } from 'lit';

type StaticContent = string | TemplateResult;
type SetDataFn<T> = (data: T) => void;

/**
 * @param setData Associate data with the currently open dialog.
 */
type TmDialogContentFn<T> = (setData: SetDataFn<T>) => StaticContent;

export type TmDialogContent<T> = StaticContent | TmDialogContentFn<T>;

export enum TmDialogButtonPosture {
    Primary = 'primary',
    Secondary = 'secondary',
    Tertiary = 'tertiary',
}

type ButtonCloseDialogFn<T = unknown> = (data?: T) => void;

export interface TmDialogButton<T = unknown> {
    label: string;
    reason: DynamicComponentExitReason;
    onClick?: (close: ButtonCloseDialogFn<T>) => void;
    posture?: TmDialogButtonPosture;
    destructive?: boolean;
    checkDisabled?: (data?: T) => boolean;
}

export type TmPrefabDialogButton = Pick<TmDialogButton, 'label' | 'reason'>;

/**
 * Configuration for modifying prefabricated buttons.
 */
export type TmDialogButtonMap = Record<
    TmDialogButtonPosture.Primary | TmDialogButtonPosture.Secondary,
    TmPrefabDialogButton
>;

export type TmDialogOrientation = 'horizontal' | 'vertical';
/**
 * Internal config shape containing all possible settings specified.
 */
export interface TmDialogServiceConfigFull<T = unknown> extends Pick<
    DynamicComponentOptions,
    'host'
> {
    buttons: TmDialogButton<T>[] | TmDialogButtonMap;
    /**
     * Describes the orientation of the dialog buttons and title.
     *
     * A vertical orientation favors a centered, stacked layout, while
     * horizontal orientations favor a side-aligned layout.
     */
    orientation: TmDialogOrientation;
    /**
     * If `true`, an 'X' close button will be displayed in the top-right corner of the dialog.
     */
    showCloseButton: boolean;
    classes: string[];
    headerIcon?: TemplateResult<2>;
    preventClose: boolean;
    showButtons: boolean;
    /**
     * Whether or not to show a divider between the title header and the dialog content.
     */
    showDivider: boolean;
}

/**
 * Consumer-facing configuration contract. Most settings are optional.
 */
export interface TmDialogServiceConfig<T = unknown> extends Partial<
    Omit<TmDialogServiceConfigFull, 'buttons'>
> {
    /**
     * Specify an array of entirely custom buttons or
     * provide a map of buttons that adhere to standard dialog events.
     */
    buttons?: TmDialogButton<T>[] | DeepPartial<Nullable<TmDialogButtonMap>>;
}

const DIALOG_CLOSE_EVENT = 'tm-dialog-close';

type TmDialogCloseEvent<T> = CustomEvent<DynamicComponentResult<T>>;

export function dispatchDialogCloseEvent<T>(element: Element, result: DynamicComponentResult<T>) {
    const closeEvent: TmDialogCloseEvent<T> = new CustomEvent(DIALOG_CLOSE_EVENT, {
        detail: result,
        bubbles: true,
        composed: true,
    });

    element.dispatchEvent(closeEvent);
}

export function listenForDialogResult<T>(
    element: Element,
    callback: (data: DynamicComponentResult<T>) => void
) {
    const listener = (e: Event) => {
        if (!(e instanceof CustomEvent)) {
            throw new Error(`Invalid event instance for "${DIALOG_CLOSE_EVENT}"`);
        }

        callback((e as TmDialogCloseEvent<T>).detail);
    };

    element.addEventListener(DIALOG_CLOSE_EVENT, listener);

    return {
        unsubscribe: () => element.removeEventListener(DIALOG_CLOSE_EVENT, listener),
    };
}
